import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Stack, Typography } from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';

import GlobalWrapper from '../../components/Wrapper/GlobalWrapper';
import GlobalDatagrid from '../../components/mui/DataGrid/GlobalDatagrid';
import GlobalDeleteDialog from '../../components/mui/Dialogue/GlobalDeleteDialog';

import { SkillxXpRules_Table_Head } from '../../components/datagrid/skillx/XpRulesTableHead';
import { getSkillxXpRules, deleteSkillxXpRule } from '../../redux/features/skillxSlice';
import AddXpRule from './xp/AddXpRule';
import UpdateXpRule from './xp/UpdateXpRule';
import { SX } from './leaderboard/leaderboardData';

/**
 * SkillX XP Settings — the admin's point table.
 *
 * Edits skillx_xp_rules, which is what every producer reads before awarding.
 * Two properties of this table drive the whole screen:
 *
 *   - it is CONFIGURATION, not data: fourteen-ish rows, fetched in one call and
 *     filtered/paged here rather than round-tripping the server for a page of
 *     ten out of fourteen;
 *   - editing it is not retroactive: each ledger row stores the value it was
 *     awarded at, so retuning a rule moves nothing that already happened. The
 *     edit dialog says so where it matters.
 */

// Retired rules are part of the configuration an admin is looking at — a rule
// that stopped awarding is exactly what someone comes here to check. Module
// scope so the object identity is stable across renders.
const dispatchparams = { includeInactive: true };

/** One header statistic. */
function StatCard({ icon, value, label, color }) {
  return (
    <Box
      sx={{
        p: 2,
        height: '100%',
        borderRadius: `${SX.radius.md}px`,
        background: SX.card,
        border: `1px solid ${SX.borderStrong}`,
        boxShadow: SX.shadowSoft,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.25}>
        <Box
          sx={{
            width: 38,
            height: 38,
            flexShrink: 0,
            borderRadius: `${SX.radius.sm}px`,
            background: `${color}14`,
            color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon icon={icon} width={20} height={20} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 19, fontWeight: 800, lineHeight: 1.1, color: SX.text }}>
            {value}
          </Typography>
          <Typography noWrap sx={{ fontSize: 11.5, color: SX.textMuted }}>
            {label}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

export default function SkillxXpSettings() {
  const dispatch = useDispatch();
  const { paginationData } = useSelector((state) => state.globalDataGrid);
  const { xpRules, loading } = useSelector((state) => state.skillx);
  const { gridSize, pageNum, search } = paginationData;

  useEffect(() => {
    dispatch(getSkillxXpRules(dispatchparams));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allRules = useMemo(() => xpRules?.data || [], [xpRules]);

  // Filter and page in the browser. The whole table arrives in one response, so
  // asking the server for a slice of fourteen rows would be a network round
  // trip to do less than `Array.prototype.slice`.
  const filtered = useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    if (!q) return allRules;
    return allRules.filter(
      (r) =>
        (r.label || '').toLowerCase().includes(q) ||
        (r.rule_key || '').toLowerCase().includes(q),
    );
  }, [allRules, search]);

  const paged = useMemo(() => {
    const size = gridSize || 10;
    const start = ((pageNum || 1) - 1) * size;
    return filtered.slice(start, start + size);
  }, [filtered, gridSize, pageNum]);

  // Edit and retire sit in the row rather than behind the anchor drawer — both
  // read the row they act on (event_count decides delete vs. deactivate), so
  // they belong next to it.
  const columns = useMemo(
    () => [
      ...SkillxXpRules_Table_Head,
      {
        field: 'actions',
        headerName: 'Actions',
        minWidth: 300,
        sortable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <UpdateXpRule rowData={params.row} dispatchparams={dispatchparams} />
            <GlobalDeleteDialog
              title="Retire XP Rule"
              buttonName="Retire"
              deleteNote={
                params.row?.event_count > 0
                  ? `"${params.row?.label}" has awarded ${params.row?.event_count} time(s). It will be deactivated rather than deleted, and those points stay exactly as they are. Continue?`
                  : `"${params.row?.label}" has never awarded anything and will be deleted. Continue?`
              }
              api={deleteSkillxXpRule(params.row?.id)}
              upDateCall={getSkillxXpRules(dispatchparams)}
              loading={loading}
            />
          </Stack>
        ),
      },
    ],
    [loading],
  );

  const stats = useMemo(() => {
    const active = allRules.filter((r) => r.is_active);
    return {
      active: active.length,
      retired: allRules.length - active.length,
      manual: active.filter((r) => r.allow_manual_award).length,
      // "Wired" in the sense that matters: something has actually awarded it.
      // The seeded rules with no producer behind them read 0 here, which is the
      // honest picture of how much of the XP system is live.
      producing: allRules.filter((r) => (r.event_count || 0) > 0).length,
      xpAwarded: allRules.reduce((sum, r) => sum + (r.xp_awarded || 0), 0),
    };
  }, [allRules]);

  return (
    <GlobalWrapper title="SkillX XP Settings" actions={<AddXpRule dispatchparams={dispatchparams} />}>
      {/* ---------- what the point table currently looks like ---------- */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={6} md={3}>
          <StatCard
            icon="solar:bolt-bold-duotone"
            value={stats.active}
            label="Active rules"
            color={SX.blue}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            icon="solar:hand-stars-bold-duotone"
            value={stats.manual}
            label="Mentors may award"
            color={SX.purple}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            icon="solar:play-circle-bold-duotone"
            value={`${stats.producing} / ${allRules.length}`}
            label="Rules that have awarded"
            color="#2E7D62"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            icon="solar:chart-2-bold-duotone"
            value={stats.xpAwarded.toLocaleString()}
            label="XP awarded to date"
            color="#C25E00"
          />
        </Grid>
      </Grid>

      <Box
        sx={{
          mb: 2,
          px: 2,
          py: 1.25,
          borderRadius: `${SX.radius.sm}px`,
          background: SX.cardSoft,
          border: `1px solid ${SX.border}`,
        }}
      >
        <Stack direction="row" alignItems="flex-start" spacing={1}>
          <Icon icon="solar:info-circle-bold-duotone" width={17} height={17} color={SX.blue} />
          <Typography sx={{ fontSize: 12.5, color: SX.textMuted }}>
            Changing a point value applies to <b>future awards only</b> — XP already earned keeps
            what it was worth, so no learner&apos;s rank moves. The student portal caches these
            values, so a change reaches learners on its next refresh.
          </Typography>
        </Stack>
      </Box>

      <Box sx={{ width: '100%', padding: 0 }}>
        <GlobalDatagrid
          tableHead={columns}
          rows={paged}
          meta={filtered.length}
          loading={loading}
          rowUniqueId="id"
          rowClick={false}
        />
      </Box>
    </GlobalWrapper>
  );
}
